import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { Pause, Play } from 'lucide-react';
import { useToken } from '@/common/helper';
import { GetNewsFlash } from '@/apis/homeAPI';
import useUnauthorize from '@/hooks/useUnauthorize';
import GlobeMonitorMap from './globe/GlobeMonitorMap';
import { monitorTripLine } from './monitorTripLine';
import NeedsAttentionRail from '../Home/map/components/NeedsAttentionRail';
import LiveTag from '../Home/map/components/LiveTag';
import { shouldUseMapboxDashboard } from '../Home/map/featureFlags';
import {
  AirportMetaEntry,
  buildNeedsAttentionItems,
  findNearbyDelayedAirports,
  type NeedsAttentionItem,
  type NewsFlashPolygonItem,
  placeShipment,
  shipmentPosition,
  type ShipmentPlace,
  StreamShipment,
} from '../Home/map/needsAttention';
import type { LaneEvent } from '../Home/map/laneEvents';
import { displayTimeZone } from './arrivalRows';
import { parseTeamParam } from './monitorTeams';
import { isNoTeamMatched, useMonitorTeams } from './useMonitorTeams';
import { useMonitorRotation } from './useMonitorRotation';
import { ARRIVALS_WINDOW_HOURS, useMonitorBoard } from './useMonitorArrivals';
import ArrivalsBoard from './ArrivalsBoard';
import TripPanel from './TripPanel';
import { buildArrivingItems, buildPickupItems } from './arrivingItems';
import { countLater, describeGroup } from './monitorRotation';
import { useMonitorTrip } from './useMonitorTrip';
import ZoomlogiWordmark from './ZoomlogiWordmark';
import './Monitor.scss';

// Hazard polygons change slowly; the dashboard fetches them once per visit.
const NEWS_FLASH_REFRESH_MS = 15 * 60 * 1000;
const CLOCK_TICK_MS = 15 * 1000;
// The stream flushes every ~250 ms; ranking (point-in-polygon per shipment per
// hazard ring, plus airport distances) only needs to keep up with the TV.
const RANKING_THROTTLE_MS = 3 * 1000;
/**
 * Overlay size on TV-sized screens, matching --monitor-ui-scale in
 * Monitor.scss (1x below 1700px). Kept close to the globe's own label size:
 * at 1.4x the panels dwarfed the map.
 */
export const TV_UI_SCALE = 1.05;
const uiScale = (): number =>
  typeof window !== 'undefined' && window.innerWidth > 1700 ? TV_UI_SCALE : 1;

// Map fits keep clear of the overlays: the rail (264px at 1rem, right), the
// arrivals board (bottom, overview) or the card (380px, left) and trip panel
// (bottom) during a spotlight.
const overviewFitPadding = (scale: number) => ({
  top: 40,
  left: 40,
  right: Math.round(300 * scale),
  bottom: Math.round(285 * scale),
});
/** Space kept between the framed route and the top of the trip panel. */
const TRIP_PANEL_GAP_PX = 24;
/** Panel height changes smaller than this do not reframe the camera. */
const TRIP_PANEL_REACH_STEP_PX = 8;
const focusFitPadding = (scale: number) => ({
  top: 60,
  left: Math.round(436 * scale),
  right: Math.round(320 * scale),
  bottom: Math.round(290 * scale),
});

// The user's profile timezone, like every date the app shows; falls back to
// the browser's when the profile has none.
const formatClock = (date: Date): string =>
  date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
    timeZone: displayTimeZone(),
  });

const countBySeverity = (items: { severity: string }[]) =>
  items.reduce(
    (acc, item) => {
      if (item.severity === 'critical') acc.critical += 1;
      else if (item.severity === 'at_risk') acc.at_risk += 1;
      else if (item.severity === 'watch') acc.watch += 1;
      return acc;
    },
    { critical: 0, at_risk: 0, watch: 0 }
  );

/**
 * "+93 more in later loops": the alerts and watch shipments (both paged
 * through loop by loop) that this loop does not show in any group: one
 * shown as arriving is not counted again for later.
 */
const railFooter = (
  items: NeedsAttentionItem[],
  queue: NeedsAttentionItem[]
): string | undefined => {
  const later = countLater(items, queue);
  return later > 0 ? `+${later} more in later loops` : undefined;
};

/** The rail's title when nothing needs attention: its first group. */
const QUIET_TITLES: Partial<Record<NeedsAttentionItem['severity'], string>> = {
  arriving: 'Arriving soon',
  watch: 'On watch',
  pickup: 'Picking up soon',
};

/** Headings over the later groups in the rail (see NeedsAttentionRail). */
const RAIL_SECTIONS: Partial<Record<NeedsAttentionItem['severity'], string>> = {
  arriving: 'Arriving soon',
  watch: 'On watch',
  pickup: 'Picking up soon',
};

interface AirportContext {
  airportMeta: Record<string, AirportMetaEntry>;
  delayThreshold: number;
}

/**
 * Full-screen TV view on a 3D globe. Rotates between an overview of every
 * shipment and a spotlight on each item of the loop (see buildLoopQueue);
 * clicking an item jumps straight to it.
 */
const MonitorScreen = () => {
  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const [shipments, setShipments] = useState<StreamShipment[]>([]);
  const pendingShipmentsRef = useRef<StreamShipment[]>([]);
  const lastRankedAtRef = useRef(0);
  const rankTimerRef = useRef<number | undefined>(undefined);
  // Leading edge right away, then at most one update per RANKING_THROTTLE_MS
  // carrying the latest snapshot.
  const handleShipmentsChange = useCallback((next: StreamShipment[]) => {
    pendingShipmentsRef.current = next;
    if (rankTimerRef.current !== undefined) return;
    const wait = lastRankedAtRef.current + RANKING_THROTTLE_MS - Date.now();
    const flush = () => {
      rankTimerRef.current = undefined;
      lastRankedAtRef.current = Date.now();
      setShipments(pendingShipmentsRef.current);
    };
    if (wait <= 0) flush();
    else rankTimerRef.current = window.setTimeout(flush, wait);
  }, []);
  useEffect(() => () => window.clearTimeout(rankTimerRef.current), []);
  const [newsFlashPolygon, setNewsFlashPolygon] = useState<
    NewsFlashPolygonItem[] | null
  >(null);
  const [laneEvents, setLaneEvents] = useState<LaneEvent[]>([]);
  const [airportContext, setAirportContext] = useState<AirportContext | null>(
    null
  );
  const [searchParams] = useSearchParams();
  const requestedTeams = useMemo(
    () => parseTeamParam(searchParams),
    [searchParams]
  );
  const teamState = useMonitorTeams(token, requestedTeams);
  const teamIds =
    teamState.status === 'ready'
      ? teamState.teams.map((team) => String(team.id))
      : undefined;
  const [clock, setClock] = useState(() => formatClock(new Date()));
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const companyName = decodedUserToken()?.company_name;
  const mapboxAvailable = shouldUseMapboxDashboard();

  useEffect(() => {
    if (!token) return undefined;
    const load = () => {
      GetNewsFlash(token)
        .then((res) => {
          if (res?.data?.data?.is_success) {
            // The API types each polygon as unknown; they are the hazard
            // polygons (hazard_type, geometry) the attention list reads.
            setNewsFlashPolygon(
              (res.data.data.data || []) as NewsFlashPolygonItem[]
            );
          }
        })
        .catch((error) => unauthorize(error));
    };
    load();
    const id = window.setInterval(load, NEWS_FLASH_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [token, unauthorize]);

  useEffect(() => {
    const id = window.setInterval(
      () => setClock(formatClock(new Date())),
      CLOCK_TICK_MS
    );
    return () => window.clearInterval(id);
  }, []);

  const items = useMemo(
    () =>
      buildNeedsAttentionItems({
        shipments,
        newsFlashPolygon,
        airportMeta: airportContext?.airportMeta,
        delayThreshold: airportContext?.delayThreshold,
        // Everything, so the TV can show the real backlog and page through it.
        maxItems: Infinity,
      }),
    [shipments, newsFlashPolygon, airportContext]
  );
  const { arrivals, pickups } = useMonitorBoard(
    teamState.status === 'loading' || isNoTeamMatched(teamState) ? null : token,
    teamIds
  );
  const arriving = useMemo(
    () => buildArrivingItems(arrivals, shipments),
    [arrivals, shipments]
  );
  const pickingUp = useMemo(
    () => buildPickupItems(pickups, shipments),
    [pickups, shipments]
  );
  const { step, progress, secondsLeft, jumpTo, queue, paused, togglePause } =
    useMonitorRotation(items, arriving, pickingUp);
  // The space bar pauses and resumes too (a keyboard or remote at the TV),
  // except where it already means something: on a button or in a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat) return;
      const { target } = event;
      if (
        target instanceof Element &&
        target.closest('button, a, input, textarea, select, [contenteditable]')
      )
        return;
      event.preventDefault();
      togglePause();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePause]);
  const counts = useMemo(() => countBySeverity(items), [items]);
  const focusShipmentId =
    step.kind === 'shipment' ? String(step.item.shipmentId) : null;
  const tripState = useMonitorTrip(token, focusShipmentId);
  // No trip panel for this spotlight: the card shows where and when instead.
  const tripUnavailable =
    focusShipmentId !== null &&
    tripState?.shipmentId === focusShipmentId &&
    Boolean(tripState.tripFailed);
  // Read once: a wall TV does not resize, and new objects would refit the map.
  const fitPadding = useMemo(() => {
    const scale = uiScale();
    return {
      overview: overviewFitPadding(scale),
      focus: focusFitPadding(scale),
    };
  }, []);
  // The trip panel grows ("On this route", the temperature line), so the
  // spotlight keeps the route clear of its real height, never less than the
  // fixed reservation. Small wobbles do not move the camera.
  const [tripPanelReach, setTripPanelReach] = useState(0);
  const handleTripPanelReach = useCallback(
    (px: number) =>
      setTripPanelReach((previous) =>
        Math.abs(previous - px) >= TRIP_PANEL_REACH_STEP_PX ? px : previous
      ),
    []
  );
  const focusPadding = useMemo(
    () => ({
      ...fitPadding.focus,
      bottom: Math.max(
        fitPadding.focus.bottom,
        tripPanelReach > 0 ? tripPanelReach + TRIP_PANEL_GAP_PX : 0
      ),
    }),
    [fitPadding, tripPanelReach]
  );
  const focusTrip =
    focusShipmentId && tripState?.shipmentId === focusShipmentId
      ? tripState.trip
      : null;
  // Where the spotlight shipment is now, from its trip: the newest of its
  // scans, sensor and stored position (the stream's alone can be days stale
  // or blank). Both maps move its marker, circle and line here; until the
  // trip loads, or when nothing places it, the stream's position stands.
  const nowLat = focusTrip?.now?.lat;
  const nowLng = focusTrip?.now?.lng;
  const focusPlace = useMemo<ShipmentPlace | null>(
    () =>
      focusShipmentId &&
      typeof nowLat === 'number' &&
      typeof nowLng === 'number' &&
      Number.isFinite(nowLat) &&
      Number.isFinite(nowLng)
        ? { shipmentId: focusShipmentId, lat: nowLat, lng: nowLng }
        : null,
    [focusShipmentId, nowLat, nowLng]
  );
  const focusedPosition = useMemo(() => {
    if (focusPlace) return { lat: focusPlace.lat, lng: focusPlace.lng };
    const match = focusShipmentId
      ? shipments.find((s) => String(s.shipment_id) === focusShipmentId)
      : undefined;
    return shipmentPosition(match);
  }, [focusPlace, focusShipmentId, shipments]);
  // The spotlight's line (solid where it has been, dashed on to the
  // destination), the same rules for both maps; null until the trip loads.
  // Rebuilt only when the trip or the position itself changes (not on the
  // minute's temperature refresh or a stream flush that left it in place),
  // since a new line reframes the map.
  const focusLat = focusedPosition?.lat;
  const focusLng = focusedPosition?.lng;
  const focusRoute = useMemo(() => {
    if (!focusShipmentId || !focusTrip) return null;
    return monitorTripLine({
      key: focusShipmentId,
      origin: focusTrip.origin,
      destination: focusTrip.destination,
      path: focusTrip.path,
      pickedUp: Boolean(focusTrip.picked_up_at),
      position:
        focusLat === undefined || focusLng === undefined
          ? null
          : { lat: focusLat, lng: focusLng },
    });
  }, [focusShipmentId, focusTrip, focusLat, focusLng]);
  const visibleAirportIcaos = useMemo(
    () =>
      findNearbyDelayedAirports(
        airportContext?.airportMeta,
        airportContext?.delayThreshold,
        focusShipmentId
          ? // Near where it is now (its trip), not a stale stored position.
            placeShipment(shipments, focusPlace).filter(
              (s) => String(s.shipment_id) === focusShipmentId
            )
          : shipments
      ),
    [airportContext, focusShipmentId, focusPlace, shipments]
  );
  // During a spotlight: the delayed airports near the shipment, in words for
  // the trip panel (the maps show their pins only), worst first.
  const nearbyAirports = useMemo(() => {
    if (!focusShipmentId) return [];
    const meta = airportContext?.airportMeta ?? {};
    return visibleAirportIcaos
      .map((icao) => ({
        code: meta[icao]?.iata || icao,
        delayPct: meta[icao]?.delayStats?.delayed_cancelled_pct ?? 0,
      }))
      .sort((a, b) => b.delayPct - a.delayPct)
      .slice(0, 3);
  }, [airportContext, focusShipmentId, visibleAirportIcaos]);

  if (!mapboxAvailable) {
    return (
      <Result
        status='info'
        title='The monitor needs the Mapbox map'
        subTitle='This view is not available with the standard map.'
      />
    );
  }

  // Never fall back to every team on a TV that asked for specific ones.
  if (teamState.status === 'loading') {
    return (
      <div className='monitor-page monitor-page--centered'>
        <Spin size='large' />
      </div>
    );
  }
  if (isNoTeamMatched(teamState)) {
    return (
      <Result
        status='warning'
        title='No matching team'
        subTitle={
          <>
            <p>
              No Shipment team you can see is called{' '}
              {teamState.missing.join(', ')}. Check the team= part of the
              address.
            </p>
            {teamState.suggestions.length > 0 && (
              <p>Did you mean: {teamState.suggestions.join(' · ')}</p>
            )}
          </>
        }
      />
    );
  }
  const teamLabel =
    teamState.status === 'ready'
      ? // A group shows its own name rather than every team's.
        teamState.labels.join(' · ')
      : null;
  const missingTeams = teamState.status === 'ready' ? teamState.missing : [];

  const group = describeGroup(step, queue);
  const shownNow = countBySeverity(queue);
  const attentionInQueue = shownNow.critical + shownNow.at_risk;
  // Every shipment the rail could list, once each (alerts, watch, arrivals,
  // pickups), for its count.
  const railTotal = new Set(
    [...items, ...arriving, ...pickingUp].map((item) => String(item.shipmentId))
  ).size;
  // Only once both sources have answered for this screen, and from the data
  // itself rather than the rotation queue (which leaves out watch items and
  // arrivals the map cannot place yet).
  const allClear =
    activeCount !== null &&
    arrivals !== null &&
    items.length === 0 &&
    !arrivals.some((a) => a.status !== 'delivered') &&
    !pickups?.length;
  const stepLabel =
    step.kind === 'overview'
      ? allClear
        ? 'Overview · all clear'
        : 'Overview'
      : group && group.position > 0
        ? `${group.label} · ${group.position} of ${group.total}`
        : (group?.label ?? 'Needs attention');
  const currentSegment = step.kind === 'overview' ? 0 : step.position;
  const segmentCount = queue.length + 1;

  return (
    <div className='monitor-page'>
      <header className='monitor-topbar'>
        <ZoomlogiWordmark className='monitor-logo' />
        {companyName && (
          <span className='monitor-company' title={companyName}>
            {companyName}
          </span>
        )}
        {teamLabel && (
          <span className='monitor-teams' title={teamLabel}>
            {teamLabel}
          </span>
        )}
        {missingTeams.length > 0 && (
          <span className='monitor-teams-missing'>
            Not found: {missingTeams.join(', ')}
            {teamState.status === 'ready' &&
              teamState.suggestions.length > 0 &&
              ` (did you mean ${teamState.suggestions[0]}?)`}
          </span>
        )}
        <span className='monitor-spacer' />
        {counts.critical + counts.at_risk > 0 && (
          <span className='monitor-counts'>
            {[
              counts.critical && `${counts.critical} critical`,
              counts.at_risk && `${counts.at_risk} at risk`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        <span className='monitor-step-label'>{stepLabel}</span>
        <span
          className='monitor-segments'
          role='progressbar'
          aria-label='Rotation progress'
          aria-valuemin={0}
          aria-valuemax={segmentCount}
          aria-valuenow={currentSegment + progress}
        >
          {Array.from({ length: segmentCount }, (_, index) => (
            <span key={index} className='monitor-segment'>
              <span
                className='monitor-segment-fill'
                // eslint-disable-next-line react/forbid-dom-props -- the width is this step's share of the loop, known only at run time
                style={{
                  width:
                    index < currentSegment
                      ? '100%'
                      : index === currentSegment
                        ? `${Math.round(progress * 100)}%`
                        : '0%',
                }}
              />
            </span>
          ))}
        </span>
        <span className='monitor-next'>
          {paused
            ? 'Paused'
            : `next in 0:${String(secondsLeft).padStart(2, '0')}`}
        </span>
        <button
          type='button'
          className={`monitor-pause${paused ? ' monitor-pause--paused' : ''}`}
          onClick={togglePause}
          aria-pressed={paused}
          title={
            paused
              ? 'Resume the rotation (space)'
              : 'Pause the rotation (space)'
          }
        >
          {paused ? (
            <Play size={16} aria-hidden='true' />
          ) : (
            <Pause size={16} aria-hidden='true' />
          )}
          <span>{paused ? 'Resume' : 'Pause'}</span>
        </button>
        <span className='monitor-divider' />
        <LiveTag badgeValue={activeCount ?? undefined} />
        <span className='monitor-clock'>{clock}</span>
      </header>
      <main className='monitor-map'>
        <GlobeMonitorMap
          newsFlashPolygon={newsFlashPolygon}
          focusShipmentId={focusShipmentId}
          tripUnavailable={tripUnavailable}
          focusRoute={focusRoute}
          focusPlace={focusPlace}
          onShipmentsChange={handleShipmentsChange}
          fitBoundsPadding={fitPadding.overview}
          focusFitPadding={focusPadding}
          teamIds={teamIds}
          onLaneEventsChange={setLaneEvents}
          onActiveCountChange={setActiveCount}
          onAirportContextChange={setAirportContext}
          visibleAirportIcaos={visibleAirportIcaos}
        />
        <NeedsAttentionRail
          items={queue}
          onSelect={jumpTo}
          activeShipmentId={focusShipmentId}
          title={
            attentionInQueue > 0
              ? 'Needs attention'
              : ((queue[0] && QUIET_TITLES[queue[0].severity]) ??
                'Arriving soon')
          }
          totalCount={railTotal}
          footer={railFooter(items, queue)}
          sectionLabels={RAIL_SECTIONS}
        />
        {allClear && step.kind === 'overview' && (
          <section className='monitor-all-clear' aria-label='All clear'>
            <span className='monitor-all-clear-title'>All clear</span>
            <span>
              Nothing needs attention, is arriving or is due for pickup in the
              next {ARRIVALS_WINDOW_HOURS} hours.
            </span>
          </section>
        )}
        {step.kind === 'shipment' && tripState && (
          <TripPanel
            data={tripState}
            position={focusedPosition}
            now={Date.now()}
            laneEvents={laneEvents}
            onReachChange={handleTripPanelReach}
            airports={nearbyAirports}
          />
        )}
        {step.kind === 'overview' && (
          <ArrivalsBoard
            arrivals={arrivals}
            windowHours={ARRIVALS_WINDOW_HOURS}
          />
        )}
      </main>
    </div>
  );
};

/**
 * One screen per team selection: changing `?team=` starts a fresh monitor, so
 * no shipments, rotation or rail state from the previous teams carry over (an
 * empty new team must not inherit the old queue, nor show "All clear" early).
 * Other params, such as `?theme=`, leave the screen running.
 */
const MonitorPage = () => {
  const [searchParams] = useSearchParams();
  const teamKey = parseTeamParam(searchParams).join(',');
  return <MonitorScreen key={teamKey} />;
};

/**
 * /monitor_v2, where the globe was first tried: the same screen as /monitor,
 * so TVs bookmarked there keep working with their query (`?team=`,
 * `?theme=`). Not a redirect: a visit stays one page view.
 */
export const MonitorGlobePage = MonitorPage;

export default MonitorPage;
